import { useMemo } from 'react';

import {
  useCreateTagKey,
  useCreateTagValue,
  useCreateTagging,
  useDeleteTagging,
  useListTaggings,
  useListTags,
  useUpdateTagValue,
} from '@/hooks/useSwr';
import {
  ResourceTag,
  ResourceTagKey,
  ResourceTagging,
  ResourceType,
  Tagging,
  TaggingCreate,
  TaggingsByResourceType,
} from '@/types/schema';
import { appOrganizationPath } from '@/utils/constants';

import { DEFAULT_RESOURCES } from './constants';

export function isResourceTag(
  resourceTag:
    | (ResourceTag & { description?: string })
    | { tag_key: string; tag_key_id: string }
    | undefined
): resourceTag is ResourceTag & { description?: string } {
  return (
    resourceTag !== undefined &&
    (resourceTag as ResourceTag).tag_value !== undefined
  );
}

const RESOURCE_TYPE_PREFIX = {
  [ResourceType.Dataset]: 'datasets',
  [ResourceType.Deployment]: 'host',
  [ResourceType.Experiment]: 'experiments',
  [ResourceType.Prompt]: 'prompts',
  [ResourceType.Project]: 'projects/p',
  [ResourceType.Queue]: 'annotation-queues',
};

export function getResourceLink(
  resourceType: ResourceType,
  resourceId: string,
  workspaceId: string | null,
  datasetId?: string
) {
  if (!workspaceId) {
    return '';
  }

  if (ResourceType.Prompt === resourceType) {
    return `/${RESOURCE_TYPE_PREFIX[resourceType]}/${resourceId}?organizationId=${workspaceId}`;
  }

  if (ResourceType.Experiment === resourceType) {
    if (datasetId) {
      return `/${appOrganizationPath}/${workspaceId}/datasets/${datasetId}/compare?selectedSessions=${resourceId}`;
    }
    return '';
  }

  return `/${appOrganizationPath}/${workspaceId}/${RESOURCE_TYPE_PREFIX[resourceType]}/${resourceId}`;
}

export const useCreateTagMutation = () => {
  const createResourceTagKeyMutation = useCreateTagKey();
  const createResourceTagValueMutation = useCreateTagValue();
  const createTaggingMutation = useCreateTagging();

  return {
    trigger: async (data: {
      key?: string;
      keyId?: string;
      value: string;
      description?: string;
      key_description?: string;
      resources: TaggingsByResourceType;
    }) => {
      // Handle form submission here
      let keyId = data.keyId;

      // Create the key if it doesn't exist
      if (data.key && !keyId) {
        try {
          const resp = await createResourceTagKeyMutation.trigger({
            json: {
              key: data.key,
              description: data.key_description,
            },
          });
          keyId = resp?.id;
        } catch (e) {
          throw new Error(`Failed to create tag key: ${(e as Error).message}`);
        }
      }

      let valueId: string | null = null;
      if (keyId) {
        try {
          const resp = await createResourceTagValueMutation.trigger({
            json: {
              value: data.value,
              description: data.description,
            },
            templateUrlParams: {
              tagKeyId: keyId,
            },
          });
          valueId = resp?.id ?? null;
        } catch (e) {
          throw new Error(
            `Failed to create tag value: ${(e as Error).message}`
          );
        }
      }

      const createPromises: Promise<ResourceTagging | undefined>[] = [];
      if (valueId) {
        Object.entries(data.resources).forEach(
          ([resourceTypePlural, resourceIds]: [string, TaggingCreate[]]) => {
            const resourceType = resourceTypePlural.slice(
              0,
              -1
            ) as ResourceType;

            resourceIds.forEach(({ resource_id }) => {
              createPromises.push(
                createTaggingMutation.trigger({
                  json: {
                    tag_value_id: valueId,
                    resource_type: resourceType,
                    resource_id: resource_id,
                  },
                })
              );
            });
          }
        );
        try {
          await Promise.all(createPromises);
        } catch (e) {
          throw new Error(
            `Failed to create resource tags. Try removing one of the resources from this tag: ${
              (e as Error).message
            }`
          );
        }
      }
    },
    isMutating:
      createResourceTagKeyMutation.isMutating ||
      createResourceTagValueMutation.isMutating ||
      createTaggingMutation.isMutating,
    error:
      createResourceTagKeyMutation.error ||
      createResourceTagValueMutation.error ||
      createTaggingMutation.error,
  };
};

export const useUpdateTagMutation = (
  existingResourceTag?: ResourceTag & { description?: string }
) => {
  const updateResourceTagValueMutation = useUpdateTagValue();
  const createTaggingMutation = useCreateTagging();
  const deleteTaggingMutation = useDeleteTagging();
  if (!existingResourceTag) {
    return {
      trigger: async () => {
        // no-op
      },
      isLoading: false,
    };
  }
  return {
    trigger: async (data: {
      value: string;
      description: string;
      resources: TaggingsByResourceType;
    }) => {
      if (
        data.description !== existingResourceTag.description ||
        data.value !== existingResourceTag.tag_value
      ) {
        try {
          await updateResourceTagValueMutation.trigger({
            json: {
              value: data.value,
              description: data.description,
            },
            templateUrlParams: {
              tagValueId: existingResourceTag.tag_value_id,
              tagKeyId: existingResourceTag.tag_key_id,
            },
          });
        } catch (e) {
          console.error(e);
          throw new Error(
            `Failed to update tag value: ${(e as Error).message}`
          );
        }
      }

      const existingTaggings: [string, Tagging[]][] = Object.entries(
        existingResourceTag.resources ?? DEFAULT_RESOURCES
      );
      const deletePromises: Promise<void>[] = [];
      const createPromises: Promise<ResourceTagging | undefined>[] = [];

      existingTaggings.forEach(([resourceTypePlural, taggings]) => {
        const newTaggings = data.resources[resourceTypePlural];
        const resourceType = resourceTypePlural.slice(0, -1) as ResourceType;
        taggings.forEach(({ tagging_id, resource_id }) => {
          if (
            !newTaggings.some(
              ({ resource_id: new_id }) => resource_id === new_id
            ) &&
            tagging_id
          ) {
            deletePromises.push(
              deleteTaggingMutation.trigger({
                templateUrlParams: {
                  taggingId: tagging_id,
                },
              })
            );
          }
        });

        newTaggings.forEach(({ resource_id: newResourceId }) => {
          if (
            !taggings.some(({ resource_id }) => resource_id === newResourceId)
          ) {
            createPromises.push(
              createTaggingMutation.trigger({
                json: {
                  tag_value_id: existingResourceTag.tag_value_id,
                  resource_type: resourceType,
                  resource_id: newResourceId,
                },
              })
            );
          }
        });
      });
      try {
        await Promise.all(deletePromises);
        await Promise.all(createPromises);
      } catch (e) {
        console.error(e);
        throw new Error(
          `Failed to update resource tags. Try removing one of the resources from this tag: ${
            (e as Error).message
          }`
        );
      }
    },
    isMutating:
      updateResourceTagValueMutation.isMutating ||
      createTaggingMutation.isMutating ||
      deleteTaggingMutation.isMutating,
    error:
      updateResourceTagValueMutation.error ||
      createTaggingMutation.error ||
      deleteTaggingMutation.error,
  };
};

export function useListGroupedTags() {
  const { data: resourceTags, isLoading: resourceTagsLoading } =
    useListTaggings();
  const { data: tagKeys, isLoading: tagKeysLoading } = useListTags();

  const groupedTags = useMemo(() => {
    return (
      tagKeys?.reduce((acc, tagKey) => {
        const resourceTagsOfKey: ResourceTag[] = tagKey.values.map(
          (tagValue) => {
            const foundResourceTag = resourceTags?.find(
              (tag) =>
                tag.tag_key_id === tagKey.id && tag.tag_value_id === tagValue.id
            );
            const resources = foundResourceTag?.resources;
            return {
              tag_key: tagKey.key,
              tag_value: tagValue.value,
              tag_value_id: tagValue.id,
              tag_key_id: tagKey.id,
              resources,
            };
          }
        );
        return {
          ...acc,
          [tagKey.id]: {
            ...tagKey,
            taggings: resourceTagsOfKey,
          },
        };
      }, {} as Record<string, ResourceTagKey & { taggings: ResourceTag[] }>) ??
      {}
    );
  }, [resourceTags, tagKeys]);

  return {
    data: groupedTags,
    isLoading: resourceTagsLoading || tagKeysLoading,
  };
}
